import { useState } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import {
  useVisitPaymentQrQuery,
  useCreatePendingPaymentMutation,
  useDeletePendingPaymentMutation,
} from '../queries';
import { QrPaymentType, ReceivedPaymentStatus, ReceivedPayment, PaymentQr } from '../entity';
import QrPaymentRow from './QrPaymentRow';
import { useFormatCurrency } from '@features/currency/currency.hooks';

type QrPaymentSwitchProps = {
  visitId: string;
  type: QrPaymentType;
  amount?: number | null;
  isPaid?: boolean;
  pendingPayment?: ReceivedPayment | null;
  clientPhone?: string | null;
  clientPhonePrefix?: string | null;
};

const LABEL_TEXT: Record<QrPaymentType, string> = {
  deposit: 'Rezervační záloha',
  servicesAndProducts: 'Služby a produkty',
  additional: 'Doplňková platba',
};

const buildShareMessage = (
  qr: PaymentQr,
  type: QrPaymentType,
  format: (amount: number) => string,
): string => {
  const typeLabel =
    type === 'deposit'
      ? 'zálohy'
      : type === 'servicesAndProducts'
        ? 'služeb a produktů'
        : 'doplňkové platby';
  return `Platba ${typeLabel}: ${format(qr.amount)}\nÚčet: ${qr.account}\nVS: ${qr.variableSymbol}`;
};

const dataUrlToFile = async (dataUrl: string, filename: string): Promise<File> => {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return new File([blob], filename, { type: blob.type || 'image/png' });
};

const QrPaymentSwitch = ({
  visitId,
  type,
  amount,
  isPaid = false,
  pendingPayment,
  clientPhone,
  clientPhonePrefix,
}: QrPaymentSwitchProps) => {
  const { t } = useIntlTranslations('qrPayment');
  const [qrOpen, setQrOpen] = useState(false);
  const { format } = useFormatCurrency();
  const [shareError, setShareError] = useState<string | null>(null);

  const isPending = pendingPayment?.status === ReceivedPaymentStatus.PENDING;

  const createPendingMutation = useCreatePendingPaymentMutation();
  const deletePendingMutation = useDeletePendingPaymentMutation();
  const isMutating = createPendingMutation.isPending || deletePendingMutation.isPending;

  const hasAmount = !!amount && amount > 0;

  const {
    data: qrData,
    isFetching: isQrLoading,
    error: qrError,
  } = useVisitPaymentQrQuery(visitId, qrOpen, type);

  const handleGenerateQr = () => {
    setQrOpen(true);
    if (hasAmount && !isMutating && !isPaid) {
      createPendingMutation.mutate({ visitId, type });
    }
  };

  const fullPhone =
    clientPhone != null && clientPhone !== ''
      ? `${clientPhonePrefix ?? '+420'}${clientPhone}`
      : null;

  const handleSmsShare = async () => {
    if (!qrData || !fullPhone) return;
    setShareError(null);
    const message = buildShareMessage(qrData, type, format);
    const filename = `qr-platba-${qrData.variableSymbol}.png`;
    try {
      const file = await dataUrlToFile(qrData.qrDataUrl, filename);
      const shareData: ShareData = { files: [file], text: message };
      if (typeof navigator !== 'undefined' && navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
        return;
      }
    } catch (err) {
      const error = err as Error;
      if (error.name === 'AbortError') return;
    }
    // Fallback: otevřít SMS aplikaci s předvyplněným číslem a textem
    window.open(`sms:${fullPhone}?body=${encodeURIComponent(message)}`, '_self');
  };

  const handleWhatsAppShare = async () => {
    if (!qrData || !fullPhone) return;
    setShareError(null);
    const message = buildShareMessage(qrData, type, format);
    const filename = `qr-platba-${qrData.variableSymbol}.png`;
    try {
      const file = await dataUrlToFile(qrData.qrDataUrl, filename);
      const shareData: ShareData = { files: [file], text: message };
      if (typeof navigator !== 'undefined' && navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
        return;
      }
    } catch (err) {
      const error = err as Error;
      if (error.name === 'AbortError') return;
    }
    // Fallback: otevřít WhatsApp s předvyplněným číslem a textem
    const phone = fullPhone.replace('+', '');
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };
  return (
    <>
      <QrPaymentRow
        label={LABEL_TEXT[type]}
        amount={format(amount)}
        isPaid={isPaid}
        isPending={isPending}
        isMutating={isMutating}
        disabled={!hasAmount}
        onClick={handleGenerateQr}
      />

      {/* Předělat na imperative dialog*/}
      {/* QR dialog */}
      <Dialog open={qrOpen} onClose={() => setQrOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{LABEL_TEXT[type]}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} alignItems="center">
            {isQrLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : qrError ? (
              <Alert severity="error">{(qrError as Error).message}</Alert>
            ) : qrData ? (
              <Stack spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
                <Box
                  component="img"
                  src={qrData.qrDataUrl}
                  alt="QR platba"
                  sx={{ width: 260, height: 260 }}
                />
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  {t('scanHint')}
                </Typography>
                <Stack spacing={0.5} sx={{ width: '100%' }}>
                  <Typography variant="body2">
                    {t('amount')}: <strong>{format(qrData.amount)}</strong>
                  </Typography>
                  <Typography variant="body2">
                    {t('variableSymbol')}: <strong>{qrData.variableSymbol}</strong>
                  </Typography>
                  <Typography variant="body2">
                    {t('account')}: <strong>{qrData.account}</strong>
                  </Typography>
                </Stack>
                {fullPhone && (
                  <Stack spacing={1} alignItems="center" sx={{ width: '100%' }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('shareHint')}
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <Tooltip title={t('sendSms')}>
                        <IconButton onClick={handleSmsShare} sx={{ bgcolor: 'info.light' }}>
                          <SmsOutlinedIcon color="info" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('sendWhatsApp')}>
                        <IconButton onClick={handleWhatsAppShare} sx={{ bgcolor: 'success.light' }}>
                          <WhatsAppIcon color="success" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    {shareError && (
                      <Alert severity="error" sx={{ width: '100%' }}>
                        {shareError}
                      </Alert>
                    )}
                  </Stack>
                )}
              </Stack>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrOpen(false)}>{t('close')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QrPaymentSwitch;
